// @ts-nocheck
/*
  EOY 2026 discovery section — sections/pb-eoy-discovery.liquid

  State: closed/open (Unbox switch) and one active thing at a time: an item
  number or the scent card.
  - Unbox switch (role="switch"): closed image <-> exploded image + hotspots,
    with the unbox animation chosen in the customizer (data-unbox: bloom |
    lift | slide | flip | zoom | wipe | sparkle) — data-anim="opening" | "closing" on the root for
    ANIM_MS; skipped for prefers-reduced-motion. There are two copies of the
    switch (desktop: under the sticker, mobile: under the image), kept in sync.
    The exploded figure is `inert` while closed, so its hotspots can't be
    reached by keyboard. Switching it off also clears the active item / scent.
  - Hotspot: click activates its item (hover too, on devices that hover).
    Below 750px it also scrolls to the item, but only when the item is not
    in view yet.
  - Item name: click activates it (again: closes it). On a closed box the
    image switches to exploded so the matching hotspot shows. No scrolling.
  - GEUR marker: activates the scent card, same rules as a hotspot.
  Default on load: closed, nothing active.
*/
(() => {
  if (customElements.get('pb-eoy-discovery')) return;

  const MOBILE_QUERY = '(max-width: 749px)';
  const HOVER_QUERY = '(hover: hover) and (pointer: fine)';
  const SCENT = 'scent';
  const ACCORDION_MS = 350; // keep in sync with the item-desc transition in the CSS
  const ANIM_MS = 3000; // longest unbox keyframe incl. delay + hotspot stagger (any style)

  class PbEoyDiscovery extends HTMLElement {
    connectedCallback() {
      this.switchButtons = Array.from(this.querySelectorAll('[data-dsc-switch]'));
      this.closedFigure = this.querySelector('[data-dsc-closed]');
      this.explodedFigure = this.querySelector('[data-dsc-exploded]');
      this.items = Array.from(this.querySelectorAll('[data-dsc-item]'));
      this.hotspots = Array.from(this.querySelectorAll('[data-dsc-hotspot]'));
      this.geur = this.querySelector('[data-dsc-geur]');
      this.scent = this.querySelector('[data-dsc-scent]');

      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      this.hoverQuery = window.matchMedia(HOVER_QUERY);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onClick = this.onClick.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerover', this.onPointerOver);

      this.open = false;
      this.active = null;
      this.setOpen(false, false);
      this.setActive(null);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerover', this.onPointerOver);
      clearTimeout(this.animTimer);
    }

    onClick(event) {
      if (event.target.closest('[data-dsc-switch]')) {
        this.setOpen(!this.open);
        return;
      }

      const toggle = event.target.closest('[data-dsc-item-toggle]');
      if (toggle) {
        const key = toggle.dataset.dscItemToggle;
        if (this.active === key) {
          this.setActive(null);
          return;
        }
        this.setActive(key);
        if (!this.open && this.explodedFigure) this.setOpen(true);
        return;
      }

      const hotspot = event.target.closest('[data-dsc-hotspot]');
      if (hotspot) {
        const key = hotspot.dataset.dscHotspot;
        this.setActive(key);
        this.revealOnMobile(this.items.find((item) => item.dataset.dscItem === key));
        return;
      }

      if (event.target.closest('[data-dsc-geur]')) {
        this.setActive(SCENT);
        this.revealOnMobile(this.scent);
      }
    }

    // Desktop: hovering a number (or the GEUR marker) activates it too.
    onPointerOver(event) {
      if (event.pointerType !== 'mouse' || !this.hoverQuery.matches || !this.open) return;

      const hotspot = event.target.closest('[data-dsc-hotspot]');
      if (hotspot) {
        this.setActive(hotspot.dataset.dscHotspot);
        return;
      }
      if (event.target.closest('[data-dsc-geur]')) this.setActive(SCENT);
    }

    setOpen(open, animate = true) {
      const next = Boolean(open) && Boolean(this.explodedFigure);
      if (animate && next !== this.open) this.playAnimation(next ? 'opening' : 'closing');

      this.open = next;
      this.dataset.state = this.open ? 'open' : 'closed';
      for (const button of this.switchButtons) button.setAttribute('aria-checked', String(this.open));

      if (this.explodedFigure) {
        this.explodedFigure.inert = !this.open;
        this.explodedFigure.setAttribute('aria-hidden', String(!this.open));
      }
      if (this.closedFigure) this.closedFigure.setAttribute('aria-hidden', String(this.open));

      // Closed box: nothing stays selected (item, hotspot, scent card).
      if (!this.open && this.active !== null) this.setActive(null);
    }

    // Restart the explode / implode keyframes (assets/pb-eoy-discovery.css).
    playAnimation(name) {
      if (this.reducedMotion.matches) return;
      clearTimeout(this.animTimer);
      delete this.dataset.anim;
      void this.offsetWidth; // reflow, so the same animation can run again
      this.dataset.anim = name;
      this.animTimer = setTimeout(() => delete this.dataset.anim, ANIM_MS);
    }

    setActive(key) {
      this.active = key;

      for (const item of this.items) {
        const on = item.dataset.dscItem === key;
        item.classList.toggle('is-active', on);
        item.querySelector('[data-dsc-item-toggle]')?.setAttribute('aria-expanded', String(on));
        // Closed descriptions stay in the DOM (collapsed by CSS, so the height
        // can animate) but are inert and hidden from assistive tech.
        const desc = item.querySelector('.pb-eoy-dsc__item-desc');
        if (desc) {
          desc.inert = !on;
          desc.setAttribute('aria-hidden', String(!on));
        }
      }

      for (const hotspot of this.hotspots) {
        const on = hotspot.dataset.dscHotspot === key;
        hotspot.classList.toggle('is-active', on);
        hotspot.setAttribute('aria-pressed', String(on));
      }

      const scentOn = key === SCENT;
      this.scent?.classList.toggle('is-active', scentOn);
      if (this.geur) {
        this.geur.classList.toggle('is-active', scentOn);
        this.geur.setAttribute('aria-pressed', String(scentOn));
      }
    }

    // Mobile only: bring the item into view when it isn't fully visible yet.
    revealOnMobile(target) {
      if (!target || !this.mobileQuery.matches) return;

      // Wait for the accordion to open (CSS transition): it changes the height.
      setTimeout(() => {
        const rect = target.getBoundingClientRect();
        const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
        if (inView) return;
        target.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
      }, ACCORDION_MS);
    }
  }

  customElements.define('pb-eoy-discovery', PbEoyDiscovery);
})();
